import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { badgeClass, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { failed, hours, lastMonth, localToday, monthName, tdClass, thClass, useShifts } from './hrShared';

type Status = 'present' | 'half_day' | 'absent' | 'paid_leave' | 'unpaid_leave' | 'holiday' | 'weekly_off';

const STATUS_LABEL: Record<Status, string> = {
  present: 'Present',
  half_day: 'Half day',
  absent: 'Absent',
  paid_leave: 'Paid leave',
  unpaid_leave: 'Unpaid leave',
  holiday: 'Holiday',
  weekly_off: 'Weekly off',
};

interface DayRow {
  employeeId: string;
  code: string;
  name: string;
  department: string | null;
  employmentType: string;
  contractorName: string | null;
  defaultShiftId: string | null;
  status: Status | null;
  shiftId: string | null;
  inTime: string | null;
  outTime: string | null;
  workedMinutes: number;
  overtimeMinutes: number;
  late: boolean;
  notes: string | null;
  marked: boolean;
  suggested: Status | null;
  locked: boolean;
}

interface Entry {
  status: Status | '';
  shiftId: string;
  inTime: string;
  outTime: string;
}

const DaySheet: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const queryClient = useQueryClient();
  const { data: shifts = [] } = useShifts();
  const [date, setDate] = useState(localToday());
  const { data, isLoading } = useQuery<{ date: string; holiday: string | null; rows: DayRow[] }>({
    queryKey: ['hr', 'day', date],
    queryFn: async () => (await api.get('/hr/attendance/day', { params: { date } })).data,
    enabled: !!date,
  });
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  useEffect(() => {
    if (data) {
      setEntries(Object.fromEntries(data.rows.map((row) => [row.employeeId, { status: row.status ?? row.suggested ?? '', shiftId: row.shiftId ?? row.defaultShiftId ?? '', inTime: row.inTime ?? '', outTime: row.outTime ?? '' }])));
    }
  }, [data]);
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const change = (id: string, patch: Partial<Entry>) => setEntries((previous) => ({ ...previous, [id]: { ...previous[id], ...patch } }));
  const everyone = (status: Status) => setEntries((previous) => Object.fromEntries(Object.entries(previous).map(([id, entry]) => [id, rows.find((row) => row.employeeId === id)?.locked ? entry : { ...entry, status: entry.status || status }])));
  const pending = rows.filter((row) => !row.locked && entries[row.employeeId]?.status);
  const save = useMutation({
    mutationFn: async () =>
      (await api.put('/hr/attendance/day', {
        date,
        entries: pending.map((row) => {
          const entry = entries[row.employeeId];
          const working = entry.status === 'present' || entry.status === 'half_day';
          return { employeeId: row.employeeId, status: entry.status, shiftId: entry.shiftId || null, inTime: working ? entry.inTime || null : null, outTime: working ? entry.outTime || null : null };
        }),
      })).data,
    onSuccess: (result) => {
      toast.success('Attendance saved');
      queryClient.setQueryData(['hr', 'day', date], result);
      queryClient.invalidateQueries({ queryKey: ['hr', 'summary'] });
    },
    onError: failed('Could not save the attendance'),
  });
  const unmarked = rows.filter((row) => !row.locked && !entries[row.employeeId]?.status).length;

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
      <div className="flex flex-wrap items-end gap-3 border-b border-gray-200 p-4">
        <label className="text-sm text-gray-700">
          Day
          <input type="date" value={date} max={localToday()} onChange={(event) => setDate(event.target.value)} aria-label="Attendance day" className={cn(inputClass, 'mt-1 w-44')} />
        </label>
        {data?.holiday && <span className={cn(badgeClass, 'bg-indigo-50 text-indigo-700')}>Holiday: {data.holiday}</span>}
        {canEdit && (
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={() => everyone('present')} className={secondaryButtonClass}>Mark the Rest Present</button>
            <button type="button" onClick={() => save.mutate()} disabled={save.isPending || pending.length === 0} className={primaryButtonClass}>{save.isPending ? 'Saving…' : `Save ${pending.length}`}</button>
          </div>
        )}
      </div>
      {isLoading ? <p className="p-8 text-center text-sm text-gray-500">Loading…</p> : rows.length === 0 ? <p className="p-8 text-center text-sm text-gray-500">Nobody was employed on this day.</p> : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr><th className={thClass}>Person</th><th className={thClass}>Status</th><th className={thClass}>Shift</th><th className={thClass}>In</th><th className={thClass}>Out</th><th className={thClass}>Worked</th><th className={thClass}>Overtime</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const entry = entries[row.employeeId] ?? { status: '', shiftId: '', inTime: '', outTime: '' };
                const working = entry.status === 'present' || entry.status === 'half_day';
                const disabled = !canEdit || row.locked;
                return (
                  <tr key={row.employeeId} data-testid="day-row" className={cn(!entry.status && 'bg-amber-50/40')}>
                    <td className={tdClass}>
                      <span className="font-mono text-xs text-gray-500">{row.code}</span> {row.name}
                      <span className="block text-xs text-gray-500">{row.employmentType === 'contract' ? `Contract · ${row.contractorName}` : row.department ?? ''}{row.locked ? ' · payroll final' : ''}</span>
                    </td>
                    <td className={tdClass}>
                      <select value={entry.status} disabled={disabled} onChange={(event) => change(row.employeeId, { status: event.target.value as Status })} aria-label={`Status of ${row.code}`} className={cn(inputClass, 'w-36')}>
                        <option value="">Not marked</option>
                        {(Object.keys(STATUS_LABEL) as Status[]).map((status) => (<option key={status} value={status}>{STATUS_LABEL[status]}</option>))}
                      </select>
                    </td>
                    <td className={tdClass}>
                      <select value={entry.shiftId} disabled={disabled || !working} onChange={(event) => change(row.employeeId, { shiftId: event.target.value })} aria-label={`Shift of ${row.code}`} className={cn(inputClass, 'w-32')}>
                        <option value="">—</option>
                        {shifts.map((shift) => (<option key={shift.id} value={shift.id}>{shift.code}</option>))}
                      </select>
                    </td>
                    <td className={tdClass}><input type="time" value={entry.inTime} disabled={disabled || !working} onChange={(event) => change(row.employeeId, { inTime: event.target.value })} aria-label={`In of ${row.code}`} className={cn(inputClass, 'w-28')} /></td>
                    <td className={tdClass}><input type="time" value={entry.outTime} disabled={disabled || !working} onChange={(event) => change(row.employeeId, { outTime: event.target.value })} aria-label={`Out of ${row.code}`} className={cn(inputClass, 'w-28')} /></td>
                    <td className={cn(tdClass, 'tabular-nums')}>{row.marked && (row.status === 'present' || row.status === 'half_day') ? hours(row.workedMinutes) : '—'}{row.late && <span className={cn(badgeClass, 'ml-2 bg-red-50 text-red-700')}>Late</span>}</td>
                    <td className={cn(tdClass, 'tabular-nums')}>{row.overtimeMinutes ? hours(row.overtimeMinutes) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && <p className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500">{unmarked} not marked. Hours, overtime and lateness are worked out when saved, from the times and the shift.</p>}
    </div>
  );
};

const MonthRegister: React.FC = () => {
  const [month, setMonth] = useState(lastMonth());
  const { data, isLoading } = useQuery<{ days: number; employees: any[] }>({ queryKey: ['hr', 'month', month], queryFn: async () => (await api.get('/hr/attendance/month', { params: { month } })).data });
  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
      <div className="flex items-end gap-3 border-b border-gray-200 p-4">
        <label className="text-sm text-gray-700">Month<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} aria-label="Register month" className={cn(inputClass, 'mt-1 w-44')} /></label>
        <span className="text-sm text-gray-500">P present · H half day · A absent · L paid leave · U unpaid leave · HO holiday · WO weekly off</span>
      </div>
      {isLoading || !data ? <p className="p-8 text-center text-sm text-gray-500">Loading…</p> : data.employees.length === 0 ? <p className="p-8 text-center text-sm text-gray-500">Nobody was employed in {monthName(month)}.</p> : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-500">Person</th>
                {Array.from({ length: data.days }, (_, index) => (<th key={index} className="px-1 py-2 text-center font-semibold text-gray-500">{index + 1}</th>))}
                <th className="px-2 py-2 text-right font-semibold text-gray-500">Present</th><th className="px-2 py-2 text-right font-semibold text-gray-500">Absent</th><th className="px-2 py-2 text-right font-semibold text-gray-500">Leave</th><th className="px-2 py-2 text-right font-semibold text-gray-500">Offs</th><th className="px-2 py-2 text-right font-semibold text-gray-500">OT h</th><th className="px-2 py-2 text-right font-semibold text-gray-500">Late</th><th className="px-2 py-2 text-right font-semibold text-gray-500">Not marked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.employees.map((employee) => (
                <tr key={employee.id} data-testid="register-row">
                  <td className="sticky left-0 whitespace-nowrap bg-white px-3 py-1.5 text-gray-900">{employee.code} {employee.name}</td>
                  {employee.days.map((letter: string, index: number) => (<td key={index} className={cn('px-1 py-1.5 text-center', letter === 'A' || letter === 'U' ? 'text-red-600' : letter === '' ? 'bg-amber-50' : 'text-gray-700')}>{letter}</td>))}
                  <td className="px-2 py-1.5 text-right tabular-nums">{employee.present}</td><td className="px-2 py-1.5 text-right tabular-nums">{employee.absent}</td><td className="px-2 py-1.5 text-right tabular-nums">{employee.leave}</td><td className="px-2 py-1.5 text-right tabular-nums">{employee.offs}</td><td className="px-2 py-1.5 text-right tabular-nums">{employee.overtimeHours}</td><td className="px-2 py-1.5 text-right tabular-nums">{employee.late}</td><td className={cn('px-2 py-1.5 text-right tabular-nums', employee.notMarked > 0 && 'font-semibold text-amber-700')}>{employee.notMarked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AttendancePanel: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const [view, setView] = useState<'day' | 'month'>('day');
  return (
    <div className="space-y-4">
      <div className="flex gap-2" role="radiogroup" aria-label="Attendance view">
        {[{ key: 'day', label: 'Mark a day' }, { key: 'month', label: 'Month register' }].map((item) => (
          <button key={item.key} type="button" role="radio" aria-checked={view === item.key} onClick={() => setView(item.key as 'day' | 'month')} className={cn('rounded-md px-3 py-1.5 text-sm font-medium ring-1 ring-inset', view === item.key ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300')}>{item.label}</button>
        ))}
      </div>
      {view === 'day' ? <DaySheet canEdit={canEdit} /> : <MonthRegister />}
    </div>
  );
};

export default AttendancePanel;
